<template>
  <TransitionScroller ref="virtualScrollContainer" id="articleVirtualScrollContainer">
    <article
      id="annotatorArticle"
      class="annotatorArticleData"
      :class="{
        'article-panel-height': myLatestRoundNumber >= 1 && !childFullView,
        'child-full-view-article': childFullView,
      }"
      ref="article"
    >
      <h5
        :class="{
          'child-full-view-head-tag': childFullView,
          ' uk-padding-remove-top uk-padding-remove-left': !childFullView,
        }"
        class="view-heading uk-text-bold uk-padding-small"
      >
        {{ heading }}
      </h5>

      <textarea
        v-if="
          enableInput && !hedgingSource && !reasoningSource && !fallaciesSource && !tonalitySource
        "
        data-cy="document-field"
        @input="handleTextChange"
        ref="articleInput"
        v-model="documentText"
        class="uk-textarea uk-width-1-1"
        placeholder="Start typing here..."
      ></textarea>

      <div
        ref="paragraph"
        v-else-if="this.counterpointLoaded"
        class="paragraphs"
        :class="{ 'child-full-view-modal': childFullView }"
      >
        <AnnotatedParagraph
          v-for="(p, idx) in articleParagraphs"
          :key="`P${idx}`"
          :ref="`P${idx}`"
          :paragraph="p"
          :node-annotations="documentNodeAnnotations"
          element-id-prefix="article"
          geometry-terminal-element-id="articleVirtualScrollContainer"
          @mouseClickNode="doMarkupClick"
          @mouseEnterNodeOrSpan="mouseEnterNode"
          @geometriesUpdated="updateParagraphGeometries"
          @paragraphMouseUp="doParagraphMouseUp"
        />
      </div>
      <div v-else class="paragraphs">
        <textarea
          data-cy="document-field"
          @input="handleTextChange"
          ref="articleInput"
          v-model="documentText"
          class="uk-textarea uk-width-1-1"
          placeholder="Start typing here..."
        ></textarea>
      </div>
      <div v-if="mainPanelView === 'article' && !hideArrow" class="arrows">
        <template v-if="counterpointLoaded">
          <AnnotationArrow
            v-for="ann in articleAnnotations"
            :key="`arr${ann._id}`"
            :annotation="ann"
            arrow-type="text"
            :ref="`annotation-arrow-${ann._id}`"
            type="text"
          />
        </template>
      </div>
    </article>
  </TransitionScroller>
</template>

<script>
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import AnnotatedParagraph from '@/components/general/AnnotatedParagraph';
import TransitionScroller from '@/components/general/TransitionScroller';
import AnnotationArrow from '@/components/annotations/AnnotationArrow';
import { EventBus } from '@/assets/js/event-bus';
import { arrayEquals, getElementAbsolutePosition } from '@/assets/js/utils';

export default {
  name: 'ArticlePanel',
  components: {
    AnnotatedParagraph,
    TransitionScroller,
    AnnotationArrow,
  },

  props: {
    article: Object,
    hideArrow: Boolean,

    heading: {
      type: String,
      default: 'Article View',
    },

    enableInput: {
      type: Boolean,
      default: false,
    },
    childFullView: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      geometries: {},
      sourceElementContainerBoundary: 0,
      documentText: '',
    };
  },

  computed: {
    ...mapState([
      'documentData',
      'counterpointData',
      'counterpointLoaded',
      'mainPanelView',
      'selectedItem',
      'counterpointGeometries',
      'counterpointOffsets',
      'showingCounterpointSidePanel',
      'currentTeam',
      'basisEdited',
      'debateNextState',
      'analyticHideShow',
      'articleParagraphs',
    ]),

    ...mapGetters([
      'hoverOutlineNodesIds',
      'documentNodeAnnotations',
      'arrowsArray',
      'documentSelectedSource',
      'myLatestRoundNumber',
      'documentAllData',
      'articleParagraphs',
    ]),

    // TODO: Hedging, fallacies, concensus represent dynamic Analysis types that will eventually have
    // to be mapped to whatever is provided through LanguageAnalytics/REQUEST_ANALYTICS
    // uncommenting as those are still used in the template
    hedgingSource() {
      return this.documentData?.sources?.some(n => n.sourceType === 'Hedging-Analytic');
    },
    fallaciesSource() {
      return this.documentData?.sources?.some(n => n.sourceType === 'Fallacies');
    },
    reasoningSource() {
      return this.documentData?.sources?.some(n => n.sourceType === 'Reasoning');
    },
    tonalitySource() {
      return this.documentData?.sources?.some(n => n.sourceType === 'Tonality');
    },
    assumptionsSource() {
      return this.documentAllData?.sources?.some(n => n.sourceType === 'Assumptions');
    },
    paragraphs() {
      let paragraphsObject = this.article.nodes?.reduce((paragraphs, node) => {
        return {
          ...paragraphs,
          [node.paragraphId]: paragraphs[node.paragraphId]
            ? [...paragraphs[node.paragraphId], node]
            : [node],
        };
      }, {});
      return Object.values(paragraphsObject);
    },

    articleParagraphs() {
      const articleNodeAnnotations = this.documentNodeAnnotations;
      const analyticData = this.documentAllData;
      let paragraphMapping = this.article.nodes?.reduce((paragraphs, node) => {
        return {
          ...paragraphs,
          [node.paragraphId]: paragraphs[node.paragraphId]
            ? [...paragraphs[node.paragraphId], node]
            : [node],
        };
      }, {});

      const paragraphs = {};
      for (const paragraphId in paragraphMapping) {
        const spans = [];
        if (this.counterpointLoaded) {
          let span = {};
          for (const node of paragraphMapping[paragraphId]) {
            if (!span.counterpointAnnotationIDs) {
              span.counterpointAnnotationIDs =
                articleNodeAnnotations[node.nodeKey].counterpointAnnotationIDs;
              span.sentenceId = node.sentenceId;
            }
            if (
              span.sentenceId != node.sentenceId ||
              !arrayEquals(
                span.counterpointAnnotationIDs,
                articleNodeAnnotations[node.nodeKey].counterpointAnnotationIDs
              )
            ) {
              span.fullText = span.nodes.map(x => x.nodeContent).join('');
              spans.push(span);
              span = {};
              span.counterpointAnnotationIDs =
                articleNodeAnnotations[node.nodeKey].counterpointAnnotationIDs;
              span.sentenceId = node.sentenceId;
            }
            span.nodes = [...(span.nodes || []), node];

            for (const source in analyticData) {
              const analyticValue = analyticData[source][node.nodeKey];
              if (analyticValue !== undefined && !span[source]) {
                let showAnalyticData = this.analyticHideShow.map(n => n.name);
                if (showAnalyticData.includes(source)) span[source] = analyticValue;
              }
            }
          }
          if (span.nodes) {
            span.fullText = span.nodes.map(x => x.nodeContent).join('');
            spans.push(span);
          }
        }

        const allAnnotationIds = new Set();
        for (const span of spans) {
          for (const annotationId of span.counterpointAnnotationIDs) {
            allAnnotationIds.add(annotationId);
          }
        }

        paragraphs[paragraphId] = {
          paragraphId,
          nodes: paragraphMapping[paragraphId],
          fullText: paragraphMapping[paragraphId].map(node => node.nodeContent).join(''),
          spans: spans,
          counterpointAnnotationIDs: Array.from(allAnnotationIds),
        };
      }
      this.setArticleParagraphs(paragraphs);
      return Object.values(paragraphs);
    },

    articleAnnotations() {
      return this.arrowsArray.filter(a => !a.refAnnotation?._id);
    },
  },

  mounted() {
    this.setSourceElementContainerBoundary();
    this.$emit('mounted', this.article);

    if (this.enableInput) {
      this.saveContentInterval = setInterval(() => {
        if (this.basisEdited) {
          this.saveText();
        }
      }, 300000);
    }

    EventBus.on('article.content.save', this.saveText);
    setTimeout(() => {
      this.$nextTick(() => {
        if (this.enableInput && this.$refs.articleInput) {
          this.$refs.articleInput.style.height = 'auto';
          this.$refs.articleInput.style.height = this.$refs.articleInput.scrollHeight + 'px';
        }
      });
    }, 1000);
  },

  beforeUnmount() {
    EventBus.off('article.content.save', this.saveText);
  },

  updated() {
    this.setSourceElementContainerBoundary();
    this.$emit('updated');
  },

  watch: {
    article: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value.content !== this.documentText) this.documentText = value?.content;
      },
    },
    enableInput(value) {
      setTimeout(() => {
        if (value && this.$refs.articleInput) {
          this.$refs.articleInput.style.height = this.$refs.articleInput.scrollHeight + 'px';
        }
      }, 2000);
    },
    counterpointData: {
      handler() {
        this.$nextTick(() => {
          EventBus.emit('counterpoint.data.updated');
        });
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions([
      'setSelectedTextNode',
      'updateDocumentSource',
      'updateSourceDocument',
      'outlineSourceDocument',
    ]),

    ...mapMutations(['setArticleParagraphs', 'setBasisEdited']),

    getScroller() {
      return this.$refs.virtualScrollContainer;
    },

    doParagraphMouseUp(ev) {
      this.$emit('textClick', {
        event: ev,
        selection: window.getSelection(),
        target: {
          ref: this,
          type: 'article',
        },
      });
    },

    doMarkupClick(node) {
      if (
        this.documentNodeAnnotations[node.nodeKey].counterpointAnnotationIDs &&
        this.documentNodeAnnotations[node.nodeKey].counterpointAnnotationIDs.length
      )
        this.$emit('markupClick', node);
    },

    hover(node) {
      if (!this.selectedItem) return;

      if (
        ['textNode', 'outlineNode'].includes(this.selectedItem.type) &&
        this.hoverOutlineNodesIds &&
        node.outlineNodes
      ) {
        return node.outlineNodes
          .map(o => o.nodeKey.split('-')[0])
          .some(o => this.hoverOutlineNodesIds.includes(o));
      }
    },

    mouseEnterNode(nodeOrSpan) {
      let node = nodeOrSpan.nodes ? nodeOrSpan.nodes[0] : nodeOrSpan;
      this.setSelectedTextNode(node);
    },

    updateParagraphGeometries(geometries) {
      for (const key in geometries) {
        this.geometries[key] = geometries[key];
      }
      this.$emit('geometriesUpdated', this.geometries);
    },

    setSourceElementContainerBoundary() {
      if (this.$refs.articleContainer) {
        let articleContainerEl = this.$refs.articleContainer;
        this.sourceElementContainerBoundary =
          getElementAbsolutePosition(articleContainerEl).left + articleContainerEl.offsetWidth - 60;
      }
    },

    handleTextChange(event) {
      this.documentSelectedSource.text = this.documentText;
      this.setBasisEdited(true);
      let element = event.target;
      if (event.inputType === 'insertFromPaste' || event.inputType === 'insertText') {
        element.style.height = element.scrollHeight + 'px';
      } else {
        element.style.height = 'auto';
        element.style.height = element.scrollHeight + 'px';
      }
    },

    async saveText(generateNLPOutline = undefined) {
      try {
        let res = {};
        if (generateNLPOutline?.generateNLPOutline === true) {
          res = await this.outlineSourceDocument({
            generateOutlineData: {
              outline: true,
            },
          });
        } else {
          res = await this.updateSourceDocument({
            documentSourceData: {
              content: this.documentText,
            },
          });
        }
        return res;
      } catch (err) {
        EventBus.emit('mainActionButton.status', LoadingStatus.FAILED);
        console.error(err);
        return null;
      } finally {
        this.setBasisEdited(false);
      }
    },
  },
};
</script>
<style lang="scss">
.article-panel-height {
  min-height: 100vh;
}

.child-full-view-modal {
  height: calc(100% - 59px);
  overflow: auto;
  padding: 0 35px 3px 40px !important;
}

.child-full-view-head-tag {
  margin: 10px 40px !important;
  padding-top: 15px !important;
  font-size: $global-font-size;
}

.child-full-view-article {
  height: 100%;
  overflow: hidden;
  padding: 0 !important;
}
</style>
